/**
 * Stat formulas from the wiki page Design → Combat rules. Numbers come from
 * the content pack; nothing here is a literal balance value.
 */
import type { ContentPack, Element, Human, Item, Monster } from '@ab/content-schema';
import { lookup } from './content.ts';
import { idiv, percent } from './determinism.ts';

export type StatName = 'hp' | 'ap' | 'patk' | 'pdef' | 'aatk' | 'adef' | 'spd';
export type StageStat = 'patk' | 'pdef' | 'aatk' | 'adef' | 'spd';

/** A human's equipped item ids. */
export type Equipment = { readonly weapon: string; readonly armor: string };

/** How a boss's stats are scaled from its species' stats at its level (the dungeon's `boss` block). */
export type BossScaling = { readonly hpPercent: number; readonly statPercent: number };

/** What a stat depends on: the species (`monster.*` or `human.*`), the level, a human's equipment and a boss's scaling. */
export type UnitLike = {
  readonly monster: string;
  readonly level: number;
  readonly equipment: Equipment | null;
  readonly boss: BossScaling | null;
};

export const speciesOf = (content: ContentPack, monster: string): Monster => {
  const m = lookup(content.monsters, monster);
  if (m === null) throw new Error(`unknown monster ${monster}`);
  return m;
};

const humanOf = (content: ContentPack, human: string): Human => {
  const h = lookup(content.humans, human);
  if (h === null) throw new Error(`unknown human ${human}`);
  return h;
};

const itemOf = (content: ContentPack, item: string): Item => {
  const i = lookup(content.items, item);
  if (i === null) throw new Error(`unknown item ${item}`);
  return i;
};

/**
 * A stat at level `L` from its base (Pokémon's formula, integer division):
 * `hp` is `2 × base × L ÷ 100 + L + 10`; any other stat `2 × base × L ÷ 100 + 5`.
 */
export const statAtLevel = (stat: StatName, base: number, level: number): number =>
  idiv(2 * base * level, 100) + (stat === 'hp' ? level + 10 : 5);

/**
 * An equipment stat: the item's `equip.stats` base at the item's own
 * `equip.level` (`statAtLevel`), whatever the wearer's level; 0 when the item
 * leaves the stat out.
 */
const equipStat = (content: ContentPack, itemId: string, stat: 'patk' | 'pdef' | 'aatk' | 'adef'): number => {
  const equip = itemOf(content, itemId).equip;
  const base = equip?.stats[stat];
  return equip === undefined || base === undefined ? 0 : statAtLevel(stat, base, equip.level);
};

/**
 * A unit's stat before stages:
 * - monster: `statAtLevel` of the species' base at the unit's level; a boss
 *   then takes `hpPercent`% of that for `hp` and `statPercent`% for every
 *   other stat;
 * - human: `hp`, `ap`, `spd` are `statAtLevel` of the human's bases at its
 *   level; `patk`/`aatk` come from the weapon, `pdef`/`adef` from the armor
 *   (`equipStat`: at the item's level, 0 when the item leaves the stat out).
 */
export const unitStat = (content: ContentPack, u: UnitLike, stat: StatName): number => {
  if (u.equipment !== null) {
    switch (stat) {
      case 'hp':
      case 'ap':
      case 'spd':
        return statAtLevel(stat, humanOf(content, u.monster).baseStats[stat], u.level);
      case 'patk':
      case 'aatk':
        return equipStat(content, u.equipment.weapon, stat);
      case 'pdef':
      case 'adef':
        return equipStat(content, u.equipment.armor, stat);
    }
  }
  const value = statAtLevel(stat, speciesOf(content, u.monster).baseStats[stat], u.level);
  if (u.boss === null) return value;
  return percent(value, stat === 'hp' ? u.boss.hpPercent : u.boss.statPercent);
};

/** The types the type chart and STAB see: the species' types for a monster, none for a human. */
export const typesOf = (content: ContentPack, u: { readonly monster: string; readonly equipment: Equipment | null }): readonly Element[] =>
  u.equipment !== null ? [] : speciesOf(content, u.monster).types;

/**
 * Stage multiplier as an integer percent: s ≥ 0 → (2 + s) × 100 ÷ 2;
 * s < 0 → 2 × 100 ÷ (2 − s). So +1 = 150, +2 = 200, −1 = 66, −2 = 50.
 */
export const stageMultiplier = (stages: number): number => {
  const s = Math.max(-6, Math.min(6, stages));
  return s >= 0 ? idiv((2 + s) * 100, 2) : idiv(2 * 100, 2 - s);
};

type Staged = { readonly stat: StageStat; readonly stages: number };

/** The total stage count of one stat, clamped to −6..+6. */
export const stageTotal = (stages: readonly Staged[], stat: StageStat): number => {
  let total = 0;
  for (const s of stages) if (s.stat === stat) total += s.stages;
  return Math.max(-6, Math.min(6, total));
};

/** A stat as used in a calculation: the unit's stat (`unitStat`) times its stage multiplier, as an integer percent. */
export const effectiveStat = (content: ContentPack, unit: UnitLike & { readonly stages: readonly Staged[] }, stat: StageStat): number =>
  percent(unitStat(content, unit, stat), stageMultiplier(stageTotal(unit.stages, stat)));

/** Experience needed to go from `level` to `level + 1`. */
export const expToNext = (content: ContentPack, level: number): number => content.rules.combat.expPerLevel * level;

/** Experience granted for defeating a unit of this level. */
export const expForDefeat = (content: ContentPack, level: number): number => content.rules.combat.expPerDefeat * level;
